import { Injectable, Injector } from '@angular/core';
import { Observable, throwError, ReplaySubject, Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { LocalStorageService } from 'src/app/services/shared/local-storage/local-storage.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { retry } from 'rxjs/operators';

// PingID
import { GenerateTokenService, Token } from 'integrationpingid-lib';
import { environment } from 'src/environments/environment';
import { NotificationService } from '../notification/notification.service';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root',
})

export class SessionService {
  private timer: any;
  private endTime: number | null = null;
  private endSessionSubject = new Subject<void>();
  private externo: any;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  host = window.location.origin;

  // OIDC session monitoring
  private tokenCheckTimer: any;
  private lastActivityTime: number = Date.now();
  private activityCleanups: (() => void)[] = [];
  private oidcRenewInProgress = false;
  private oidcWarningShown = false;
  private readonly WARN_BEFORE_EXPIRY_MS = 3 * 60 * 1000; // Mostrar warning 3 min antes de expirar
  private readonly CHECK_INTERVAL_MS = 30 * 1000; // Verificar cada 30 segundos
  private readonly INACTIVITY_THRESHOLD_MS = 5 * 60 * 1000; // Considerar inactivo después de 5 min sin actividad

  constructor(
    private injector: Injector,
    private http: HttpClient,
    private router: Router,
    public serviceToken: GenerateTokenService,
    private localStorageService: LocalStorageService,
  ) {
    this.showConsoleLog();
  }

  private isVipContext(): boolean {
    return this.localStorageService.getIsVip || this.router.url.includes('/vip/');
  }

  public isVipSession(): boolean {
    return this.isVipContext();
  }

  showConsoleLog() {
    if (environment.PRODUCTION.toString().toLowerCase() === "true") {
      window.console.log = function () {
        // This is intentional
      }; // disable any console.log debugging statements in production mode
    }
  }

  startSession(): void {
    console.log('environment', environment);
    const isVipFlow = this.isVipContext();
    console.log('[Session Flow] startSession -> flujo activo:', isVipFlow ? 'VIP/OIDC' : 'INTERNO/PingID');

    // Para VIP/OIDC: usar monitor basado en la expiración real del JWT
    if (isVipFlow) {
      this.startOidcSessionMonitor();
      return;
    }

    // PingID: mantener lógica original
    let startTime = Date.now();
    let timerSession = Number("6600000"); // pingid: 7200000 2 horas | 6600000 1:50 hora
    this.endTime = startTime + timerSession;

    this.timer = setInterval(() => {
      const actualTime = Date.now();
      const restanteTiempo = this.endTime !== null ? this.endTime - actualTime : 0;
      console.log("restanteTiempo ------>", actualTime, restanteTiempo);

      if (restanteTiempo <= 1200) {
        this.endSession();
        this.endSessionSubject.next();
      }

    }, timerSession);
  }

  /**
   * Monitor de sesión OIDC basado en la expiración real del JWT y actividad del usuario.
   *
   * Estrategia:
   * - Trackea actividad del usuario (clicks, teclado, mouse, etc.)
   * - Renueva el token automáticamente (sin modal) si el usuario está activo
   * - Si el usuario está inactivo y el token expira, muestra warning y hace logout
   * - El renew se activa SOLO cuando hay actividad, no en idle
   */
  startOidcSessionMonitor(): void {
    console.log('[OIDC Session] Iniciando monitor de sesión basado en actividad del usuario');
    this.oidcWarningShown = false;
    this.initActivityTracking();

    // Ejecutar una verificación inmediata para no esperar 30s al primer chequeo.
    this.evaluateOidcTokenExpiry();

    this.tokenCheckTimer = setInterval(() => {
      this.evaluateOidcTokenExpiry();
    }, this.CHECK_INTERVAL_MS);
  }

  private evaluateOidcTokenExpiry(): void {
    const expirationMs = this.getOidcTokenExpirationMs();
    if (!expirationMs) {
      console.log('[OIDC Session] No se pudo obtener expirationMs, abortando chequeo.');
      return;
    }

    const timeUntilExpiry = expirationMs - Date.now();
    const isActive = this.isUserActive();

    if (environment.PRODUCTION.toString().toLowerCase() !== "true") {
      console.log(`[OIDC Session] Token expira en ${Math.floor(timeUntilExpiry / 60000)} min ${Math.floor((timeUntilExpiry % 60000) / 1000)} seg | Usuario activo: ${isActive}`);
    }

    if (timeUntilExpiry <= 0) {
      // Token expirado
      console.log('[OIDC Session] Token expirado, ejecutando logout inmediato.');
      this.stopOidcSessionMonitor();
      this.handleLogout();
    } else if (timeUntilExpiry <= this.WARN_BEFORE_EXPIRY_MS) {
      // Token expira en menos de 3 minutos
      if (isActive && !this.oidcRenewInProgress) {
        // Usuario activo: renovar automáticamente en background sin salir de DM
        console.log('[OIDC Session] Usuario activo cerca de expiración, intentando renovación interna (refresh token).');
        this.oidcRenewInProgress = true;
        this.stopOidcSessionMonitor();
        this.handleRefreshOidcInternal();
        return;
      }

      if (!isActive) {
        // Usuario inactivo: mostrar warning una sola vez y mantener monitor activo
        // para forzar logout/redirect cuando realmente expire el token.
        if (!this.oidcWarningShown) {
          console.log('[OIDC Session] Usuario inactivo cerca de expiración, mostrando modal de warning.');
          console.log('[Session Debug] Emitiendo warning modal por inactividad. timeUntilExpiry(ms):', timeUntilExpiry, '| route:', this.router.url);
          this.oidcWarningShown = true;
          this.endSessionSubject.next();
        }
      }
    } else {
      // Token aún lejos de expirar: mantener monitoreo activo
      // Si hay actividad reciente, el monitor seguirá verificando
    }
  }

  /**
   * Registra listeners de actividad del usuario para detectar si está activo.
   *
   * Eventos monitoreados:
   * - click, keydown: Interacción explícita del usuario
   * - mousemove, scroll: Navegación en la página
   * - touchstart: Dispositivos móviles
   * - focus: Usuario enfoca la ventana del navegador
   * - visibilitychange: Usuario retorna a la pestaña
   *
   * Nota: Se excluyen eventos pasivos como resize para evitar ruido.
   */
  private initActivityTracking(): void {
    const updateActivity = () => {
      this.lastActivityTime = Date.now();
      if (environment.PRODUCTION.toString().toLowerCase() !== "true") {
        console.log('[Activity] Usuario activo detectado');
      }
    };

    // Eventos que indican actividad explícita del usuario
    const activeEvents = ['click', 'keydown', 'keyup', 'change'];
    // Eventos de navegación/visualización (con throttling via passive listeners)
    const passiveEvents = ['mousemove', 'scroll', 'touchstart', 'touchmove'];
    // Eventos de enfoque
    const focusEvents = ['focus', 'visibilitychange'];

    const registerEvent = (event: string, handler: EventListener, passive: boolean = false) => {
      const options = passive ? { passive: true } as AddEventListenerOptions : undefined;
      document.addEventListener(event, handler, options);
    };

    // Registrar eventos activos
    activeEvents.forEach(event => registerEvent(event, updateActivity, false));

    // Registrar eventos pasivos (con passive listeners para mejor rendimiento)
    passiveEvents.forEach(event => registerEvent(event, updateActivity, true));

    // Registrar eventos de enfoque
    focusEvents.forEach(event => registerEvent(event, updateActivity, false));

    // Guardar cleanup para remover todos los listeners al finalizar
    this.activityCleanups.push(() => {
      [...activeEvents, ...passiveEvents, ...focusEvents].forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    });

    console.log('[OIDC Session] Activity tracking inicializado para eventos: click, keydown, keyup, change, mousemove, scroll, touchstart, touchmove, focus, visibilitychange');
  }

  /**
   * Obtiene la fecha de expiración (en ms) del access token OIDC almacenado.
   */
  getOidcTokenExpirationMs(): number | null {
    try {
      const oidcStorage = localStorage.getItem('oidc-token-storage');
      if (!oidcStorage) {
        return this.getStoredTokenExpirationMs();
      }

      const tokenData = JSON.parse(oidcStorage);
      const rawAccessToken = tokenData?.accessToken;
      const token = typeof rawAccessToken === 'string'
        ? rawAccessToken
        : rawAccessToken?.accessToken;

      if (!token) {
        return this.getStoredTokenExpirationMs();
      }

      try {
        const decoded: any = jwtDecode(token);
        if (decoded?.exp) {
          return decoded.exp * 1000;
        }
      } catch {
        // Access token opaco (no JWT): usar expiración persistida como fallback
      }

      return this.getStoredTokenExpirationMs();
    } catch (e) {
      console.error('Error reading OIDC token expiration:', e);
      return this.getStoredTokenExpirationMs();
    }
  }

  private getStoredTokenExpirationMs(): number | null {
    const storedExpiration = localStorage.getItem('token_expiration');
    if (!storedExpiration) {
      return null;
    }

    const expirationMs = Date.parse(storedExpiration);
    if (Number.isNaN(expirationMs)) {
      return null;
    }

    return expirationMs;
  }

  /**
   * Renueva la sesión OIDC.
   *
   * Para flujos VIP/OIDC, la renovación ocurre redirigiendo al login de Commerce-CIAM.
   * Si el usuario tiene sesión activa con el IdP, la renovación será casi transparente.
   *
   * Guarda la URL actual para retornar después de la re-autenticación.
   * El flag `oidcRenewInProgress` permite detectar si la renovación es automática.
   */
  handleRefreshOidc(): void {
    console.log('[OIDC Session] Iniciando renovación de sesión...');

    // Guardar URL actual para retornar después de re-autenticación
    const currentPath = this.router.url;
    localStorage.setItem('oidc-return-url', currentPath);
    localStorage.setItem('oidc-auto-renew', 'true'); // Flag para detectar auto-renew en callback

    // Redirigir a Commerce-CIAM para renovar token VIP
    const commerceUrl = this.getVipCommerceUrl();
    if (commerceUrl) {
      console.log('[OIDC Session] Redirigiendo a Commerce-CIAM para renovación:', commerceUrl);
      window.location.href = commerceUrl;
    } else {
      console.error('[OIDC Session] No existe URL de Commerce-CIAM para renovación VIP.');
      this.handleLogout();
    }
  }

  /**
   * Acción de "Continuar sesión" desde el modal VIP.
   * Intenta refresh interno del token para evitar redirección completa a CIAM.
   * Si falla, aplica fallback a redirección de renovación en Commerce-CIAM.
   */
  handleContinueVipSession(): void {
    if (!this.isVipContext()) {
      console.warn('[OIDC Session] handleContinueVipSession invocado fuera de flujo VIP.');
      return;
    }

    if (this.oidcRenewInProgress) {
      console.log('[OIDC Session] Renovación VIP ya en progreso, se ignora click duplicado.');
      return;
    }

    console.log('[OIDC Session] Continuar sesión VIP desde modal: intentando refresh interno sin redirección.');
    this.oidcRenewInProgress = true;
    this.stopOidcSessionMonitor();
    this.handleRefreshOidcInternal();
  }

  /**
   * Intenta renovar el token VIP/OIDC dentro de DM usando refresh_token.
   * Si no hay refresh_token o falla la llamada, hace fallback a redirect a Commerce-CIAM.
   */
  private handleRefreshOidcInternal(): void {
    const refreshToken = this.getOidcRefreshToken();
    if (!refreshToken) {
      console.warn('[OIDC Session] No existe refresh_token para VIP. Fallback a Commerce-CIAM.');
      this.oidcRenewInProgress = false;
      this.handleRefreshOidc();
      return;
    }

    const tokenEndpoint = this.getVipRefreshEndpoint();
    const clientId = localStorage.getItem('oidc-idToken-clientid') || undefined;

    if (!tokenEndpoint) {
      console.warn('[OIDC Session] No existe endpoint de token para refresh VIP. Fallback a Commerce-CIAM.');
      this.oidcRenewInProgress = false;
      this.handleRefreshOidc();
      return;
    }

    const params = new URLSearchParams();
    params.set('grant_type', 'refresh_token');
    params.set('refresh_token', refreshToken);
    if (clientId) {
      params.set('client_id', clientId);
    }

    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    };

    console.log('[OIDC Session] Intentando refresh interno VIP en endpoint OIDC:', tokenEndpoint, '| grant_type=refresh_token | client_id presente:', !!clientId);
    this.http.post<any>(tokenEndpoint, params.toString(), httpOptions)
      .pipe(
        retry({
          count: 2,
          delay: 1500,
        }),
      )
      .subscribe({
      next: (resp) => {
        const refreshed = this.applyOidcRefreshResponse(resp);
        if (!refreshed) {
          console.warn('[OIDC Session] Respuesta de refresh sin access_token válido. Fallback a Commerce-CIAM.');
          this.oidcRenewInProgress = false;
          this.handleRefreshOidc();
          return;
        }

        console.log('[OIDC Session] Refresh interno VIP exitoso. Se reinicia monitor de sesión.');
        this.oidcRenewInProgress = false;
        this.oidcWarningShown = false;
        this.startOidcSessionMonitor();
      },
      error: (error) => {
        console.warn('[OIDC Session] Error en refresh interno VIP. Fallback a Commerce-CIAM.', error);
        this.oidcRenewInProgress = false;
        this.handleRefreshOidc();
      }
    });
  }

  /**
   * Resuelve endpoint de refresh VIP priorizando el issuer OIDC recibido desde Portal.
   * Ejemplo: https://login-uat.equifax.ca/as/token
   */
  private getVipRefreshEndpoint(): string | null {
    const oidcIssuer = (localStorage.getItem('oidc-idtoken-issuer') || '').trim();
    const issuerTokenEndpoint = oidcIssuer
      ? `${oidcIssuer.replace(/\/+$/, '')}/token`
      : null;

    const oauthUrl = this.localStorageService.configInit?.["F_INIT_TOKEN_CONTRIBUTION_URL"];
    const legacyUrl = this.localStorageService.configInit?.["F_SECOND_BASE_URL"] || environment.SECOND_BASE_URL;
    return issuerTokenEndpoint || legacyUrl || oauthUrl || null;
  }

  private getOidcRefreshToken(): string | null {
    const fromStorage = localStorage.getItem('oidc_refresh_token');
    if (fromStorage) {
      if (environment.PRODUCTION.toString().toLowerCase() !== "true") {
        console.log('[OIDC Session] Refresh token encontrado en oidc_refresh_token (localStorage). Longitud:', fromStorage.length);
      }
      return fromStorage;
    }

    const fromLegacyStorage = localStorage.getItem('refresh_token');
    if (fromLegacyStorage) {
      if (environment.PRODUCTION.toString().toLowerCase() !== "true") {
        console.log('[OIDC Session] Refresh token encontrado en refresh_token (localStorage legacy). Longitud:', fromLegacyStorage.length);
      }
      return fromLegacyStorage;
    }

    try {
      const oidcStorage = localStorage.getItem('oidc-token-storage');
      if (!oidcStorage) {
        if (environment.PRODUCTION.toString().toLowerCase() !== "true") {
          console.warn('[OIDC Session] oidc-token-storage no existe; no hay refresh token para renew interno.');
        }
        return null;
      }

      const tokenData = JSON.parse(oidcStorage);
      const refreshCandidates: any[] = [
        tokenData?.refresh_token,
        tokenData?.refreshToken,
        tokenData?.tokenObj?.refresh_token,
        tokenData?.tokenObj?.refreshToken,
      ];

      const refreshToken = refreshCandidates.find((item) => typeof item === 'string' && item.trim().length > 0) || null;

      if (environment.PRODUCTION.toString().toLowerCase() !== "true") {
        console.log('[OIDC Session] Búsqueda de refresh token en oidc-token-storage. Keys:', Object.keys(tokenData || {}), '| refreshToken encontrado:', !!refreshToken);
      }

      return refreshToken || null;
    } catch {
      if (environment.PRODUCTION.toString().toLowerCase() !== "true") {
        console.warn('[OIDC Session] Error parseando oidc-token-storage durante búsqueda de refresh token.');
      }
      return null;
    }
  }

  private applyOidcRefreshResponse(resp: any): boolean {
    const newAccessToken = resp?.access_token;
    if (!newAccessToken) {
      return false;
    }

    localStorage.setItem('access_token', newAccessToken);

    const newRefreshToken = resp?.refresh_token || null;
    if (newRefreshToken) {
      localStorage.setItem('oidc_refresh_token', newRefreshToken);
    }

    try {
      const oidcStorage = localStorage.getItem('oidc-token-storage');
      const tokenData = oidcStorage ? JSON.parse(oidcStorage) : {};

      const rawAccessToken = tokenData?.accessToken;
      if (typeof rawAccessToken === 'string') {
        tokenData.accessToken = newAccessToken;
      } else {
        tokenData.accessToken = {
          ...(rawAccessToken || {}),
          accessToken: newAccessToken,
        };
      }

      if (newRefreshToken) {
        const rawRefreshToken = tokenData?.refreshToken;
        if (typeof rawRefreshToken === 'string') {
          tokenData.refreshToken = newRefreshToken;
        } else {
          tokenData.refreshToken = {
            ...(rawRefreshToken || {}),
            refreshToken: newRefreshToken,
          };
        }
      }

      if (resp?.id_token) {
        const rawIdToken = tokenData?.idToken;
        if (typeof rawIdToken === 'string') {
          tokenData.idToken = resp.id_token;
        } else {
          tokenData.idToken = {
            ...(rawIdToken || {}),
            idToken: resp.id_token,
          };
        }
      }

      localStorage.setItem('oidc-token-storage', JSON.stringify(tokenData));
    } catch (e) {
      console.warn('[OIDC Session] No se pudo actualizar oidc-token-storage tras refresh interno:', e);
    }

    let expirationDate: Date | null = null;
    try {
      const decoded: any = jwtDecode(newAccessToken);
      if (decoded?.exp) {
        expirationDate = new Date(decoded.exp * 1000);
      }
    } catch {
      // Puede ser token opaco; usar expires_in/issued_at de respuesta OAuth
    }

    if (!expirationDate && resp?.expires_in) {
      const issuedAtMs = Number(resp?.issued_at);
      const baseMs = Number.isFinite(issuedAtMs) ? issuedAtMs : Date.now();
      const expiresInSec = Number(resp.expires_in);
      if (Number.isFinite(expiresInSec) && expiresInSec > 0) {
        expirationDate = new Date(baseMs + expiresInSec * 1000);
      }
    }

    if (expirationDate) {
      localStorage.setItem('token_expiration', expirationDate.toISOString());
      console.log('[OIDC Session] Nuevo access token expira en:', expirationDate.toLocaleString());
    } else {
      console.warn('[OIDC Session] No se pudo determinar expiración del nuevo access_token tras refresh.');
    }

    return true;
  }

  private getVipCommerceUrl(): string | null {
    const fromConfigInit = this.localStorageService.configInit?.["F_OKTA_COMMERCE_URL"];
    const fromEnvironment = environment.OKTA_COMMERCE_URL;
    const selected = fromConfigInit || fromEnvironment || null;
    if (environment.PRODUCTION.toString().toLowerCase() !== "true") {
      const source = fromConfigInit ? 'configInit.F_OKTA_COMMERCE_URL' : (fromEnvironment ? 'environment.OKTA_COMMERCE_URL' : 'none');
      console.log('[OIDC Session] getVipCommerceUrl source:', source, '| value:', selected);
    }
    return selected;
  }

  /**
   * Indica si el usuario ha tenido actividad reciente.
   *
   * @param thresholdMs - Umbral en milisegundos. Por defecto: 5 minutos (INACTIVITY_THRESHOLD_MS)
   * @returns true si la última actividad fue dentro del threshold, false si lleva más inactivo
   */
  isUserActive(thresholdMs: number = this.INACTIVITY_THRESHOLD_MS): boolean {
    const timeSinceLastActivity = Date.now() - this.lastActivityTime;
    const isActive = timeSinceLastActivity < thresholdMs;

    if (environment.PRODUCTION.toString().toLowerCase() !== "true") {
      const minSinceActivity = Math.floor(timeSinceLastActivity / 60000);
      const secSinceActivity = Math.floor((timeSinceLastActivity % 60000) / 1000);
      console.log(`[Activity] Últimas actividad hace ${minSinceActivity}:${secSinceActivity.toString().padStart(2, '0')} | Activo: ${isActive}`);
    }

    return isActive;
  }

  /**
   * Detiene el monitor de sesión OIDC y limpia los listeners de actividad.
   */
  stopOidcSessionMonitor(): void {
    if (this.tokenCheckTimer) {
      clearInterval(this.tokenCheckTimer);
      this.tokenCheckTimer = null;
    }
    this.activityCleanups.forEach(cleanup => cleanup());
    this.activityCleanups = [];
  }

  endSession(): void {
    clearInterval(this.timer);
    this.endTime = null;
    this.stopOidcSessionMonitor();
  }

  onEndSession(): Observable<void> {
    // console.log('onEndSession()');
    return this.endSessionSubject.asObservable();
  }

  handleLogout(): void {
    // console.log('handleLogout()');
    const isVipFlow = this.isVipContext();
    const commerceUrl = isVipFlow ? (this.getVipCommerceUrl() || '/') : null;
    sessionStorage.clear();
    localStorage.clear();
    if (isVipFlow) {
      console.log('[OIDC Session] Logout VIP -> Commerce-CIAM:', commerceUrl);
      window.location.href = commerceUrl!;
      return;
    }
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    localStorage.clear();
    sessionStorage.clear();
    this.router.navigateByUrl(environment.URL_RETURN_APP).then(() => window.location.reload());
    // setTimeout(() => {
    //   this.router.navigate(['login']);
    // }, 2500);
  }

  handleRefreshPingId() {
    // console.log('handleRefreshPingId()');
    // const notifier = this.injector.get(NotificationService);
    this.postTokenRefreshPingId()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((resp) => {
        // console.log("resp ----->", resp);
        localStorage.removeItem('tokenAuth');
        localStorage.setItem('tokenAuth', resp.access_token);
        localStorage.removeItem('access_token');
        localStorage.setItem('access_token', resp.access_token);

        let tokenObj = localStorage.getItem('tokenObj');
        if (tokenObj) {
          let tokenObjJson = JSON.parse(tokenObj);
          tokenObjJson.access_token = resp.access_token;
          localStorage.setItem('tokenObj', JSON.stringify(tokenObjJson));
        }

        //notifier.muestraMensaje("sesión iniciada");
        // this.startSession();
      });
  }

  postTokenRefreshPingId() {
    // console.log('postTokenRefreshPingId()');
    let token: Token = this.serviceToken.getTokenUser();
    let refresh = token.refresh_token || '';
    const params = new URLSearchParams();
    params.set('grant_type', 'refresh_token');
    params.set('refresh_token', refresh);

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    };

    const secondBaseURL = this.localStorageService.configInit?.["F_SECOND_BASE_URL"] || environment.SECOND_BASE_URL;

    return this.http.post<any>(
      secondBaseURL,
      params.toString(),
      httpOptions
    );
  }

  postTokenRefreshPingIdRdsInterno(refresh_token: string, accessToken?: string | null) {
    // console.log('postTokenRefreshPingIdRdsInterno()');
    const params = new URLSearchParams();
    params.set('grant_type', 'refresh_token');
    params.set('refresh_token', refresh_token);

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      }),
    };
    const secondBaseURL = this.localStorageService.configInit?.["F_SECOND_BASE_URL"] || environment.SECOND_BASE_URL;

    return this.http.post<any>(
      secondBaseURL,
      params.toString(),
      httpOptions
    );
  }

  /**
   * Renueva el token de Oidc/VIP.
   * Los tokens VIP se reciben directamente del sistema externo via URL
   * y se almacenan en localStorage. La renovación se gestiona verificando
   * la expiración y redirigiendo al portal si es necesario.
   */
  renewTokenOidc(): void {
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      try {
        const decoded: any = jwtDecode(accessToken);
        const now = Math.floor(Date.now() / 1000);
        if (decoded.exp && decoded.exp > now) {
          console.log('[SessionService] Token Oidc/VIP aún válido, exp:', new Date(decoded.exp * 1000));
          return;
        }
      } catch (e) {
        console.warn('[SessionService] No se pudo decodificar access_token para verificar expiración');
      }
    }
    // Token expirado o no disponible -> redirigir a Commerce-CIAM
    console.warn('[SessionService] Token Oidc/VIP expirado o no disponible, redirigiendo a Commerce-CIAM...');
    const notifier = this.injector.get(NotificationService);
    notifier.muestraMensaje("Sesion expirada, redirigiendo a Commerce-CIAM..");
    const commerceUrl = this.getVipCommerceUrl() || '/';
    console.log('[OIDC Session] renewTokenOidc -> Commerce-CIAM:', commerceUrl);
    window.location.href = commerceUrl;
  }

  goToHome() {
    // console.log('goToHome()');
    if (this.isVipContext()) {
      this.router.navigateByUrl(environment.URL_RETURN_APP_VIP).then(() => window.location.reload());
    } else if (this.localStorageService.getIsExternoRDS) {
      // this.router.navigateByUrl(environment.URL_RETURN_APP_RDS).then(() => window.location.reload());
    } else {
      sessionStorage.clear();
      this.router.navigateByUrl(environment.URL_RETURN_APP).then(() => window.location.reload());
    }
  }

  logoutSessionGoToHome() {
    // console.log('logoutSessionGoToHome()');
    if (this.isVipContext()) {
      const commerceUrl = this.getVipCommerceUrl() || '/';
      console.log('[OIDC Session] logoutSessionGoToHome VIP -> Commerce-CIAM:', commerceUrl);
      window.location.href = commerceUrl;
    } else if (this.localStorageService.getIsExternoRDS) {
    } else {
      localStorage.clear();
      sessionStorage.clear();
      this.logoutRedirectLogin();
      // this.router.navigateByUrl(environment.URL_RETURN_APP).then(() => window.location.reload());
    }
  }

  logoutRedirectLogin() {
    const urlReturnApp = environment.URL_RETURN_APP;
    // console.log("this.host out ----->", this.host);
    const redirectUri = `${this.host}/callback`;

    // Validar que loginInit exista antes de hacer destructuring
    if (!this.localStorageService.loginInit) {
      console.warn('loginInit no está disponible, redirigiendo directamente a:', urlReturnApp);
      this.router.navigateByUrl(urlReturnApp).then(() => window.location.reload());
      return;
    }

    const { clientId, firstBaseUrl, secondBaseUrl } = this.localStorageService.loginInit;
    // console.log('urlReturnApp, firstBaseUrl, secondBaseUrl, redirectUri, clientId', urlReturnApp, firstBaseUrl, secondBaseUrl, redirectUri, clientId);
    this.serviceToken.verifyRedirectLogin(
      urlReturnApp,
      firstBaseUrl,
      secondBaseUrl,
      redirectUri,
      clientId
    ); // comentar para que local funcione sin autenticar
    let statusTokenUser = this.serviceToken.statusTokenUser();
    // console.log("this.statusTokenUser out", statusTokenUser);
  }

  /**
   * Valida el token de PingID y redirige al login si está expirado.
   * Este método se llama desde el interceptor HTTP en cada petición.
   * @param clickOnLink - Indica si la validación viene de un click en un link (más estricta)
   */
  tokenPingIDValidationSession(clickOnLink: boolean = false): void {
    // Skip validation for VIP (uses Oidc) and RDS external users
    if (this.isVipContext() || this.localStorageService.getIsExternoRDS) {
      return;
    }

    const token = this.extractAccessToken();

    if (this.isTokenExpired(token, clickOnLink)) {
      console.warn('Token expirado o no autenticado. Redirigiendo a la página de login.');
      this.logoutSessionGoToHome();
      throwError(() => new Error('Token expirado.'));
    }
  }

  /**
   * Extrae el access_token del localStorage de manera segura.
   * @returns El access_token o null si no existe o hay error al parsear
   */
  private extractAccessToken(): string | null {
    try {
      const tokenObj = localStorage.getItem('tokenObj');
      if (!tokenObj) {
        console.warn('No se encontró tokenObj en localStorage - Usuario no autenticado');
        return null;
      }

      const tokenObjJson = JSON.parse(tokenObj);
      const accessToken = tokenObjJson?.access_token || null;

      if (!accessToken) {
        console.warn('tokenObj existe pero no contiene access_token');
      }

      return accessToken;
    } catch (e) {
      console.error('Error parsing tokenObj from localStorage:', e);
      return null;
    }
  }

  /**
   * Verifica si el token JWT ha expirado.
   * @param token - El token JWT a validar
   * @param clickOnLink - Si true, valida incluso en rutas de autenticación
   * @returns true si el token está expirado o es inválido, false si es válido
   */
  isTokenExpired(token: string | null, clickOnLink: boolean = false): boolean {
    // Si no hay token (no autenticado o error)
    if (!token) {
      // Solo permitir acceso sin token en rutas de autenticación
      // (login, callback, páginas de error de autenticación)
      if (!clickOnLink && this.isAuthenticationRoute()) {
        return false; // Permitir acceso para completar el flujo de login
      }
      // En cualquier otra ruta, requiere autenticación
      console.warn('Token no disponible - Se requiere autenticación');
      return true;
    }

    // Si hay token, SIEMPRE validar su expiración
    // (incluso en rutas de autenticación para evitar tokens expirados)
    return this.isJWTExpired(token);
  }

  /**
   * Determina si la ruta actual es parte del flujo de autenticación.
   * Estas rutas pueden accederse sin token válido porque son parte del proceso de login.
   * @returns true si es ruta de autenticación, false si requiere token válido
   */
  private isAuthenticationRoute(): boolean {
    const currentUrl = this.router.url;

    // Rutas exactas del flujo de autenticación
    const authRoutes = [
      environment.URL_RETURN_APP,  // Ruta de retorno después de login
      '/',                          // Home (puede ser redireccionado a login)
      '/fedsso/as/token.oauth2'    // Endpoint de PingID para tokens
    ];

    // Verificar rutas exactas
    if (authRoutes.includes(currentUrl)) {
      return true;
    }

    // Verificar rutas que contienen patrones de autenticación
    const authPatterns = [
      '/callback',      // Callback de PingID/Oidc
      '/login',         // Página de login
      '/auth',          // Cualquier ruta de autenticación
      '/logout'         // Página de logout
    ];

    return authPatterns.some(pattern => currentUrl.includes(pattern));
  }

  /**
   * Verifica si un JWT ha expirado decodificando su payload.
   * @param token - El token JWT a validar
   * @returns true si está expirado, false si está vigente
   */
  private isJWTExpired(token: string): boolean {
    try {
      const decodedToken: any = jwtDecode(token);

      // Si no tiene campo exp, considerarlo inválido
      if (!decodedToken.exp) {
        console.warn('Token JWT sin campo exp (expiración)');
        return true;
      }

      // Convertir timestamp Unix a milisegundos
      const expirationDate = new Date(decodedToken.exp * 1000);
      const currentDate = new Date();

      // Margen de 5 segundos para evitar problemas de sincronización
      const EXPIRATION_MARGIN_MS = 5000;
      const expirationWithMargin = expirationDate.getTime() - EXPIRATION_MARGIN_MS;

      // Logging para debugging (puedes comentar en producción)
      if (environment.PRODUCTION.toString().toLowerCase() !== "true") {
        const timeUntilExpiration = expirationWithMargin - currentDate.getTime();
        const minutesUntilExpiration = Math.floor(timeUntilExpiration / 60000);
        console.log(`Token expira en: ${minutesUntilExpiration} minutos`);
      }

      return currentDate.getTime() > expirationWithMargin;
    } catch (e) {
      console.error('Error decodificando el JWT:', e);
      // Si no se puede decodificar, considerarlo expirado por seguridad
      return true;
    }
  }

}
